// assistantAnswerPopover.js — THE LANDED ANSWER ON THE MINIMIZED ISLAND
// (AI_ASSISTANT_SPEC v1 §1 "minimized — the bottombar only; an answer offers
// 'Open in main' / 'Open in sidebar'"; DESIGN-WL-ASSISTANT-MAIN-VIEW-1).
//
// While the reasoning pill (shared _simone/ReasoningPill.js) is the thread's
// handle, an answer that LANDS (the turn settles) turns its head row into
// "Answer ready" with the two moves: Open in main (the SAME thread in the main
// area of any page, §12.2) and Open in sidebar (the drawer). It never says so
// by itself: only an answer that arrived after the island mounted counts (a
// reload's restored thread does not), once per answer; Esc, ✕, an outside
// press and either move dismiss it.
//
// 2026-10-28 (Nika: "in minimized mode, captions should be like bubble and
// should not resize ai navbar"): these rules used to drive a SECOND capsule —
// components/AssistantAnswerPopover.js, a glass popover measured and stacked
// over the pill — and they now drive the island's own head. The names keep
// their history; `assistantAnswerPopoverBottom` (the stacking measure) left
// with the capsule.
//
// Frank note: el.call serializes each registered export on its own (FA204),
// so every export here is self-contained.

// The newest settled assistant answer's id, or null while a turn runs.
export const assistantLatestAnswerId = function assistantLatestAnswerId(root) {
  const r = root && typeof root === 'object' ? root : {}
  const list = Array.isArray(r.chatMessages) ? r.chatMessages : []
  const typing = !!r.chatTyping
  if (typing) return null
  for (let i = list.length - 1; i >= 0; i--) {
    const m = list[i]
    if (!m || typeof m !== 'object') continue
    if (m.role === 'user') return null
    // a setup note (the first thread's plan and summary) is no answer
    if (m.metadata && m.metadata.kind === 'note') continue
    if (m.role === 'assistant' && !m.local) return m.streaming ? null : String(m.id || 'm' + i)
  }
  return null
}

// The answer the island offers, or null: the island is the thread's handle —
// the minimized drawer (open, docked 'minimized') or the slot handle (the
// drawer kept shut for a page sheet, `_assistantPillHandle`) — an answer that
// landed after `baseline` (the newest answer when the island mounted), not
// dismissed.
export const assistantAnswerPopoverFor = function assistantAnswerPopoverFor(root, baseline) {
  const r = root && typeof root === 'object' ? root : {}
  const minimized = !!r.assistantOpen && r.assistantDock === 'minimized'
  const slot = !r.assistantOpen && !!r._assistantPillHandle
  const dismissed = r._aiAnswerPopoverDismissed
  const id = this.call('assistantLatestAnswerId', r)
  if ((!minimized && !slot) || !id) return null
  if (id === baseline || id === dismissed) return null
  return id
}

// Dismiss it for this answer (the next answer offers it again).
export const assistantAnswerPopoverDismiss = function assistantAnswerPopoverDismiss() {
  const root = this && this.state && this.state.root
  if (!root || typeof root.update !== 'function') return false
  const id = this.call('assistantLatestAnswerId', root)
  if (!id || root._aiAnswerPopoverDismissed === id) return false
  root.update({ _aiAnswerPopoverDismissed: id }, { preventFetch: true })
  return true
}

// Open in main — the SAME thread in the main area of the page the person is
// on (the home's grid area, or the page sheet — AI_ASSISTANT_SPEC §12.2).
export const assistantAnswerToMain = function assistantAnswerToMain() {
  this.call('assistantAnswerPopoverDismiss')
  return !!this.call('assistantThreadToMain')
}

// Open in sidebar — the drawer, on the right edge for the session; the
// person's choice of dock is unchanged. A drawer that was CLOSED reaches the
// right edge through the shell's opener promotion (the false→true edge of
// assistantOpen); one that is already open as the minimized handle has no
// edge to fire, so the session write is made here — the island's own hit
// area writes the same.
export const assistantAnswerToSidebar = function assistantAnswerToSidebar() {
  const root = this && this.state && this.state.root
  if (!root || typeof root.update !== 'function') return false
  this.call('assistantAnswerPopoverDismiss')
  const patch = { assistantOpen: true, _homeThreadOpen: false }
  if (root.assistantOpen && root.assistantDock === 'minimized') patch.assistantDock = 'right'
  root.update(patch)
  return true
}

// The island's Esc: a row of the shell keyboard registry, live only while the
// answer shows on an island that is on screen (it stays mounted while hidden).
export const assistantAnswerPopoverShortcuts = function assistantAnswerPopoverShortcuts(el) {
  const view = el || this
  if (!view || typeof view.call !== 'function') return () => false
  view.call('listShortcuts')
  const off = view.call('registerShortcut', {
    id: 'assistantAnswerPopoverClose',
    keys: 'escape',
    label: 'workspace.assistantPopover.close',
    group: 'shell.headerTabs.dashboard',
    groupOrder: 3,
    order: 9,
    when: (h, root) => {
      const node = view.node
      if (!node || node.isConnected === false) return false
      if (typeof node.getClientRects === 'function' && node.getClientRects().length === 0) {
        return false
      }
      return !!view.call('assistantAnswerPopoverFor', root, view.__answerBaseline)
    },
    run: () => view.call('assistantAnswerPopoverDismiss')
  })
  const stop = () => (typeof off === 'function' ? !!off() : false)
  view.__popoverShortcutsOff = stop
  return stop
}
